import React, { useMemo, useState } from "react";

import { Stack } from "@mui/material";
import EmptyCardDataRooms from "assets/images/EmptyCardDataRooms.png";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import Link from "components/Plaid/Link";
import { useNavigate } from "hooks";
import { usePlaid } from "hooks/plaid-hooks/usePlaid";
import { IInvestor } from "store/types";

import { ConnectionCard, ConnectionCardProps } from "../../components/data-rooms/Card";
import {
  useDeleteConnectionMutation,
  useSyncInvestmentHoldings
} from "../../queries/query-mutations/plaid-query-mutations";
import { useGetConnections } from "../../queries/query-selectors/plaid-query-selectors";

export const Plaid: React.FC = () => {
  const [syncItem, setSyncItem] = useState<string>("");
  const { mutate: doSyncItem, error: syncError, isLoading: synching } = useSyncInvestmentHoldings();
  const { data: connections, isLoading } = useGetConnections();
  const { mutate: deleteConnection } = useDeleteConnectionMutation();
  const [investor, setInvestor] = useState<IInvestor | null>(null);

  const { open, ready } = usePlaid((itemId: string) => {
    setSyncItem(itemId);
    return doSyncItem(itemId);
  });

  const navigate = useNavigate();

  const connectionsPreview = useMemo(() => {
    return (connections || []).map<ConnectionCardProps["item"]>((conn) => ({
      id: conn.id,
      username: conn.investor.name,
      title: conn.institution_name,
      subtitle: "",
      last_connected: conn.last_sync,
      updated_at: conn.updated_at,
      last_login_error_timestamp: syncItem === conn.item_id && syncError !== null ? new Date().getTime() : null,
      status:
        syncItem === conn.item_id && synching ? "loading" : syncItem === conn.item_id && syncError ? "error" : "ok",
      logo: undefined
    }));
  }, [connections, syncItem, synching, syncError]);

  return (
    <AsyncContentMui spinnerProps={{ size: 150 }} isLoading={isLoading}>
      <Stack gap="22px" sx={{ flexGrow: 1, height: "100%" }}>
        {!connectionsPreview.length ? (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.plaid.title}
            description={{
              text: EMPTY_CARD_CONTENT.plaid.description,
              externalText: EMPTY_CARD_CONTENT.plaid.externalDescription
            }}
            image={{
              src: EmptyCardDataRooms,
              sx: { width: 315, height: 140 }
            }}
            actionsElement={
              <Link
                plaidReady={ready}
                variant="contained"
                investor={investor}
                setInvestor={setInvestor}
                onConnect={() => open(undefined, investor?.id)}
                sx={{ bgcolor: "#071F35" }}
              />
            }
          />
        ) : (
          <>
            <Stack flexDirection="row" justifyContent="flex-end">
              <Link
                plaidReady={ready}
                variant="contained"
                investor={investor}
                setInvestor={setInvestor}
                onConnect={() => open(undefined, investor?.id)}
              />
            </Stack>
            <Stack direction="row" gap="22px" flexWrap="wrap">
              {connectionsPreview.map((connection) => (
                <ConnectionCard
                  key={connection.id}
                  item={connection}
                  onClick={() => navigate({ route: "plaidConnection", params: { connection: connection.id } })}
                  onConnect={() => open(connection.id as number)}
                  onDelete={() => deleteConnection(connection.id as number)}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </AsyncContentMui>
  );
};
